import {
  isEligibleForBiomarkersCrossSellMed,
  isEligibleForNadCrossSellMed,
  isEligibleForSermorelinCrossSellMed,
  isShowMetabolicForMenCrossSellMedAfterMedCrossSell,
  isShowMetabolicForMenCrossSellMedBeforeBiomarkersTestKit,
  isShowMetabolicForMenCrossSellMedReplaceBiomarkersTestKit,
  isShowMetabolicForWomenCrossSellMedAfterMedCrossSell,
  isShowMetabolicForWomenCrossSellMedBeforeBiomarkersTestKit,
  isShowMetabolicForWomenCrossSellMedReplaceBiomarkersTestKit,
  isShowNadCrossSellMedBeforeMetabolic,
  isShowSermorelinCrossSellMedBeforeMetabolic,
  isUpsEx363MedReplaceBiomarkersActive,
  isUpsEx364MedBeforeMetabolicActive,
  isUpsEx366NadBeforeMetabolicActive,
} from "@utils/userSegment/features";
import {
  PEPTIDE_SERMORELIN_PRODUCT_NAME,
  RX_NAD_PLUS_PRODUCT_NAME,
} from "@utils/addonEligibility";

export enum PostSignupCrossSellAdId {
  NAD = "nad",
  SERMORELIN = "sermorelin",
  METABOLIC = "metabolic",
  BIOMARKERS = "biomarkers",
}

type PostSignupCrossSellAdCategory = "med" | "otc";

export type PostSignupCrossSellAd = {
  id: PostSignupCrossSellAdId;
  category: PostSignupCrossSellAdCategory;
  layer: string;
};

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    postSignupCrossSellQueue?: readonly PostSignupCrossSellAd[];
  }
}

const MAX_MED_ADS = 1;
const MAX_TOTAL_ADS = 2;

const LAYERS = {
  nad: "offers-shop-nad",
  sermorelin: "offers-shop-sermorelin",
  metabolicMen: "offers-shop-metabolic-for-men",
  metabolicWomen: "offers-shop-metabolic-for-women",
  biomarkers: "offers-shop-biomarkers-test-kit",
} as const;

function ad(
  id: PostSignupCrossSellAdId,
  category: PostSignupCrossSellAdCategory,
  layer: string
): PostSignupCrossSellAd {
  return { id, category, layer };
}

function hasAd(
  selected: readonly PostSignupCrossSellAd[],
  id: PostSignupCrossSellAdId
) {
  return selected.some((selectedAd) => selectedAd.id === id);
}

function hasMedAd(selected: readonly PostSignupCrossSellAd[]) {
  return selected.some((selectedAd) => selectedAd.category === "med");
}

function resolveMetabolicAd(selected: readonly PostSignupCrossSellAd[]) {
  if (hasMedAd(selected)) {
    if (isShowMetabolicForMenCrossSellMedAfterMedCrossSell()) {
      return ad(PostSignupCrossSellAdId.METABOLIC, "otc", LAYERS.metabolicMen);
    }
    if (isShowMetabolicForWomenCrossSellMedAfterMedCrossSell()) {
      return ad(
        PostSignupCrossSellAdId.METABOLIC,
        "otc",
        LAYERS.metabolicWomen
      );
    }
    return null;
  }

  if (
    isShowMetabolicForMenCrossSellMedReplaceBiomarkersTestKit() ||
    isShowMetabolicForMenCrossSellMedBeforeBiomarkersTestKit()
  ) {
    return ad(PostSignupCrossSellAdId.METABOLIC, "otc", LAYERS.metabolicMen);
  }

  if (
    isShowMetabolicForWomenCrossSellMedReplaceBiomarkersTestKit() ||
    isShowMetabolicForWomenCrossSellMedBeforeBiomarkersTestKit()
  ) {
    return ad(PostSignupCrossSellAdId.METABOLIC, "otc", LAYERS.metabolicWomen);
  }

  return null;
}

function resolveBiomarkersAd(selected: readonly PostSignupCrossSellAd[]) {
  if (!isEligibleForBiomarkersCrossSellMed()) {
    return null;
  }

  if (
    hasAd(selected, PostSignupCrossSellAdId.METABOLIC) &&
    isUpsEx363MedReplaceBiomarkersActive()
  ) {
    return null;
  }

  return ad(PostSignupCrossSellAdId.BIOMARKERS, "otc", LAYERS.biomarkers);
}

type PostSignupCrossSellAdConfig = {
  id: PostSignupCrossSellAdId;
  category: PostSignupCrossSellAdCategory;
  resolve: (
    selected: readonly PostSignupCrossSellAd[]
  ) => PostSignupCrossSellAd | null;
  productName?: string;
  shouldEvaluate?: () => boolean;
};

/** Ranked ads. New product = new row; experiment lives in `shouldEvaluate` / `resolve`. */
const POST_SIGNUP_CROSS_SELL_ADS: PostSignupCrossSellAdConfig[] = [
  {
    id: PostSignupCrossSellAdId.NAD,
    category: "med",
    productName: RX_NAD_PLUS_PRODUCT_NAME,
    shouldEvaluate: () =>
      isUpsEx366NadBeforeMetabolicActive() && isEligibleForNadCrossSellMed(),
    resolve: () =>
      isShowNadCrossSellMedBeforeMetabolic()
        ? ad(PostSignupCrossSellAdId.NAD, "med", LAYERS.nad)
        : null,
  },
  {
    id: PostSignupCrossSellAdId.SERMORELIN,
    category: "med",
    productName: PEPTIDE_SERMORELIN_PRODUCT_NAME,
    shouldEvaluate: () =>
      isUpsEx364MedBeforeMetabolicActive() &&
      isEligibleForSermorelinCrossSellMed(),
    resolve: () =>
      isShowSermorelinCrossSellMedBeforeMetabolic()
        ? ad(PostSignupCrossSellAdId.SERMORELIN, "med", LAYERS.sermorelin)
        : null,
  },
  {
    id: PostSignupCrossSellAdId.METABOLIC,
    category: "otc",
    resolve: resolveMetabolicAd,
  },
  {
    id: PostSignupCrossSellAdId.BIOMARKERS,
    category: "otc",
    resolve: resolveBiomarkersAd,
  },
];

function hasCapacityFor(
  config: PostSignupCrossSellAdConfig,
  selected: readonly PostSignupCrossSellAd[]
) {
  if (selected.length >= MAX_TOTAL_ADS) {
    return false;
  }

  return (
    config.category !== "med" ||
    selected.filter(({ category }) => category === "med").length < MAX_MED_ADS
  );
}

export function getPostSignupCrossSellQueue() {
  return POST_SIGNUP_CROSS_SELL_ADS.reduce<PostSignupCrossSellAd[]>(
    (selected, config) => {
      if (!hasCapacityFor(config, selected)) {
        return selected;
      }

      const nextAd = config.resolve(selected);
      return nextAd ? [...selected, nextAd] : selected;
    },
    []
  );
}

export function getNextPostSignupCrossSellAd(
  currentAdId: PostSignupCrossSellAdId,
  queue: readonly PostSignupCrossSellAd[]
) {
  const currentIndex = queue.findIndex(({ id }) => id === currentAdId);
  return currentIndex >= 0 ? queue[currentIndex + 1] ?? null : null;
}

export function getPostSignupMedProductsToEvaluate() {
  return POST_SIGNUP_CROSS_SELL_ADS.flatMap((config) =>
    config.productName && config.shouldEvaluate?.() ? [config.productName] : []
  );
}
